import { useLocation } from 'react-router';
import Stack from '@mui/material/Stack';
import TableRow from '@mui/material/TableRow';
import Typography from '@mui/material/Typography';

import { IPortfolioItemReportFragment } from 'graphql/types';
import { TableCell } from 'common/components/Table';
import { UserAvatar } from 'user/components/UserAvatar';
import { ScoringChips } from 'common/components/ScoringChips';
import { ChipGroup } from 'common/components/Chip';
import { formatDate } from 'common/utils/formatDate';
import { useGlobalDrawer } from 'common/hooks/useGlobalDrawer';
import { IGlobalDrawerType } from 'common/types';
import { ReceivedCertificateProgressChip } from 'common/components/ReceivedCertificateProgressChip';

interface IProps {
    reportItem: IPortfolioItemReportFragment;
}

export const PortfolioReportTableRow = ({ reportItem }: IProps) => {
    const location = useLocation();
    const { openGlobalDrawer } = useGlobalDrawer();

    const { id, title, user, scoring, startDate, endDate, metadata } =
        reportItem;
    const { name } = user || {};
    const { validationStatus } = metadata || {};

    const startDateString = startDate && formatDate(startDate, 'dd MMMM yyyy');
    const endDateString = endDate && formatDate(endDate, 'dd MMMM yyyy');

    if (!user) return null;

    const onClickRow = () => {
        openGlobalDrawer(
            {
                type: IGlobalDrawerType.PortfolioItem,
                itemId: id,
            },
            location
        );
    };

    return (
        <TableRow
            hover
            key={id}
            sx={{ cursor: 'pointer' }}
            onClick={onClickRow}
        >
            <TableCell>
                <Stack
                    direction="row"
                    spacing={2}
                    sx={{ alignItems: 'center' }}
                >
                    <UserAvatar size={35} user={user} />
                    <Typography variant="body2">{name}</Typography>
                </Stack>
            </TableCell>
            <TableCell>{title}</TableCell>
            <TableCell sx={{ whiteSpace: 'nowrap' }}>
                {startDateString}
            </TableCell>
            <TableCell sx={{ whiteSpace: 'nowrap' }}>
                <Typography variant="body2">{endDateString}</Typography>
            </TableCell>
            <TableCell>
                {!!scoring?.length && (
                    <ChipGroup>
                        <ScoringChips scoring={scoring.filter(Boolean)} />
                    </ChipGroup>
                )}
            </TableCell>
            <TableCell>
                {validationStatus && (
                    <ReceivedCertificateProgressChip
                        endDate={endDate}
                        startDate={startDate}
                        validationStatus={validationStatus}
                    />
                )}
            </TableCell>
        </TableRow>
    );
};
